import React, { FunctionComponent } from 'react';
import { relatedRecruitment } from '../../../types/recruitmentShow';
import { CommonModal } from '../../organisms/commonModal';
import { ProfilesStepBar } from '../profilesStepBar';
import { CareerEmploymentType, ProfileStep } from '../../../types/personShow';
import { PersonShowModalIntroduction } from '../personShowModalIntroduction';
import { PersonShowModalSlogan } from '../personShowModalSlogan';
import classnames from 'classnames';
import { PersonShowModalActivity } from '../personShowModalActivity';
import { useFlash } from '../../../hooks/useFlash';
import { Loading } from '../../atoms/loading';
import { FlashBox } from '../../atoms/flashBox';
import { PersonShowModalFavoriteBrand } from '../personShowModalFavoriteBrand';
import { PersonShowModalHistories } from '../personShowModalHistories';
import { keyMap } from './constants';
import { ModalProfileState } from './types';
import { PersonShowModalResumes } from '../personShowModalResumes';
import { useDevice } from '../../../hooks/device';

type Props = {
  isConnectCompleteModalOpen: boolean;
  personId: number | null;
  isLoggedIn: boolean;
  isCompany: boolean;
  hasRequiredProfiles: boolean;
  needProfileUpdate: boolean;
  requiredProfileNames: string[];
  relatedRecruitments: relatedRecruitment[];
  companyName: string;
  handleClickModalBg: () => void;
  profileImmediateInputFlag: boolean;
  enforceFlag: boolean;
  birthdayList: number[];
  startYearList: number[];
  endYearList: number[];
  careerEmploymentTypes: CareerEmploymentType[];
  flashState: ReturnType<typeof useFlash>;
  isLoading: boolean;
  missingProfiles: ProfileStep[];
  currentIndex: number;
  currentStep: ProfileStep;
  completedKeys: string[];
  modalState: ModalProfileState;
  handleChangeModalState: <K extends keyof ModalProfileState>(
    key: K,
    value: ModalProfileState[K]
  ) => void;
  isCurrentStepSubmittable: boolean;
  handleNext: () => void;
  handleSkip: () => void;
  handleChangeStep: (index: number) => void;
};

export const Presentation: FunctionComponent<Props> = ({
  isConnectCompleteModalOpen,
  personId,
  isLoggedIn,
  isCompany,
  hasRequiredProfiles,
  needProfileUpdate,
  requiredProfileNames,
  relatedRecruitments,
  companyName,
  handleClickModalBg,
  profileImmediateInputFlag,
  enforceFlag,
  birthdayList,
  startYearList,
  endYearList,
  careerEmploymentTypes,
  flashState,
  isLoading,
  missingProfiles,
  currentIndex,
  currentStep,
  completedKeys,
  modalState,
  handleChangeModalState,
  isCurrentStepSubmittable,
  handleNext,
  handleSkip,
  handleChangeStep,
}) => {
  const showProfileSection =
    isLoggedIn &&
    !isCompany &&
    hasRequiredProfiles &&
    needProfileUpdate;
  const shouldShowFooter = showProfileSection && profileImmediateInputFlag;

  const handleProfileChange = (name: string, value: any) => {
    const modalKey = keyMap[name as ProfileStep['key']] ?? (name as keyof ModalProfileState);
    handleChangeModalState(modalKey, value);
  };

  return (
    <>
      <div className={classnames('common-loading', 'blur', { 'is-active': isLoading })}>
        <Loading />
      </div>
      <FlashBox
        flash={flashState.flash}
        flashType={flashState.flashType}
        flashMessage={flashState.flashMessage}
        handleFlash={flashState.handleFlash}
      />
      <CommonModal
        isOpen={isConnectCompleteModalOpen}
        onClose={handleClickModalBg}
        title="エントリーが完了しました"
        className="is-wide"
        innerClassName={classnames(
          'common__modal__inner--sp',
          {
            'modal-connect--recruitment flat': !(profileImmediateInputFlag && needProfileUpdate),
            'common__modal__inner--no-footer': !shouldShowFooter,
          }
        )}
        footer={shouldShowFooter ? (
          <>
            {!enforceFlag && (
              <div className="btnCover">
                <p
                  onClick={handleSkip}
                  className="clear gtm-button-view-count"
                  data-event-name="button on screen"
                  data-event="click"
                  data-category="recruitmentcontent"
                  data-value="after_entry_skip_profile"
                >
                  スキップ
                </p>
              </div>
            )}
            <div className="btnCover">
              <button
                onClick={handleNext}
                className="submit gtm-button-view-count"
                disabled={!isCurrentStepSubmittable || isLoading}
                data-event-name="button on screen"
                data-event="click"
                data-category="recruitmentcontent"
                data-value={enforceFlag ? 'after_entry_edit_profile_enforce' : 'after_entry_edit_profile'}
              >
                保存して次へ
              </button>
            </div>
          </>
        ) : undefined}
        footerClassName={classnames({ single: enforceFlag, })}
      >
        {/* ------------------- パターンA：プロフィール未完了 ------------------- */}
        {showProfileSection ? (
            profileImmediateInputFlag ? (
              missingProfiles.length > 0 && (
                <div className="profile-stepper">
                  <p className="profile-stepper-title">{companyName}が選考で見るプロフィールを入力して、応募情報を充実させよう！</p>
                  <ProfilesStepBar enforceFlag={enforceFlag} steps={missingProfiles} currentIndex={currentIndex} completedKeys={completedKeys} onChangeStep={handleChangeStep} />
                  <div className="profile-stepper__content">
                    {currentStep.key === 'introduction' && (
                      <PersonShowModalIntroduction
                        target={currentStep.key}
                        body={modalState.introduction}
                        onChangedBody={handleProfileChange}
                        fromEntry={true}
                      />
                    )}
                    {currentStep.key === 'educational_histories' && (
                      <PersonShowModalHistories
                        target="educationalHistories"
                        histories={modalState.educationalHistories}
                        onChangedBody={handleProfileChange}
                        clickTarget={0}
                        startYearList={startYearList}
                        endYearList={endYearList}
                        birthdayList={birthdayList}
                        handleDeleteHistories={()=>{}}
                        fromEntry={true}
                      />
                    )}
                    {currentStep.key === 'career_histories' && (
                      <PersonShowModalHistories
                        target="careerHistories"
                        histories={modalState.careerHistories}
                        onChangedBody={handleProfileChange}
                        clickTarget={0}
                        startYearList={startYearList}
                        endYearList={endYearList}
                        birthdayList={birthdayList}
                        careerEmploymentTypes={careerEmploymentTypes}
                        handleDeleteHistories={()=>{}}
                        fromEntry={true}
                      />
                    )}
                    {currentStep.key === 'favorite_brands' && (
                      <PersonShowModalFavoriteBrand
                        target={currentStep.key}
                        favoriteBrands={modalState.favoriteBrands}
                        onChangedBody={handleProfileChange}
                      />
                    )}
                    {currentStep.key === 'activity' && (
                      <PersonShowModalActivity
                        target={currentStep.key}
                        body={modalState.activity}
                        onChangedBody={handleProfileChange}
                        fromEntry={true}
                      />
                    )}
                    {currentStep.key === 'slogan' && (
                      <PersonShowModalSlogan
                        target={currentStep.key}
                        body={modalState.slogan}
                        onChangedBody={handleProfileChange}
                        fromEntry={true}
                      />
                    )}
                    {currentStep.key === 'resume' && (
                      <PersonShowModalResumes
                        target={currentStep.key}
                        resume={modalState.resume}
                        job_resume={modalState.job_resume}
                        onChangedBody={handleProfileChange}
                        isPc={useDevice().isPc}
                        deleted={false}
                        loading={false}
                      />
                    )}
                    {currentStep.key === 'job_resume' && (
                      <PersonShowModalResumes
                        target={currentStep.key}
                        resume={modalState.resume}
                        job_resume={modalState.job_resume}
                        onChangedBody={handleProfileChange}
                        isPc={useDevice().isPc}
                        deleted={false}
                        loading={false}
                      />
                    )}
                  </div>
                </div>
              )
            ) : (
              <div className="modal-connect-heading-updated-bottom">
                <p className="profileTitle">
                  {companyName}が選考で見るプロフィール項目はこちら
                </p>

                <div className="flexBox">
                  {requiredProfileNames.map((name, index) => (
                    <p className="required" key={index}>
                      {name}
                      {index !== requiredProfileNames.length - 1 ? ' / ' : ''}
                    </p>
                  ))}
                </div>

                <div className="modal-connect-heading-updated-bottom-button">
                  {personId && (
                    <a
                      href={`/users/${personId}`}
                      className="button button-block button-submit gtm-button-view-count"
                      data-event-name='button on screen'
                      data-event="click"
                      data-category="recruitmentcontent"
                      data-value="edit_profile"
                    >
                      プロフィールを編集する
                    </a>
                  )}
                  <button className="button-cancel" onClick={handleClickModalBg}>
                    閉じる
                  </button>
                </div>
              </div>
            )
        ) : (
          /* ------------------- パターンB：関連求人表示 ------------------- */
          <>
            <div className="modal-connect-heading-updated-related">
              <p>お探しの条件に近い求人はこちら</p>
            </div>

            <div className="modal-connect-heading-updated-inner">
              {relatedRecruitments?.map((r) => (
                <a
                  key={r.id}
                  href={`/recruitments/${r.id}`}
                  className="modal-connect-heading-updated-inner-item gtm-recruitment-view-count"
                  data-event-name='recruitments on screen'
                  data-event="click"
                  data-category="recruitmentcontent"
                  data-value="after_entry_related_recruitments"
                  data-recruitmentid={r.id}
                  data-companyid={companyName}
                >
                  <div className="modal-connect-heading-updated-inner-item-left">
                    <img src={r.image} alt={r.title} />
                  </div>

                  <div className="modal-connect-heading-updated-inner-item-right">
                    <div className="modal-connect-heading-updated-inner-item-right-title">
                      {r.title}
                    </div>
                    <div className="modal-connect-heading-updated-inner-item-right-company">
                      {r.companyName}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </>
        )}
      </CommonModal>
    </>
  );
};
